import React from 'react';
import { Link } from 'react-router-dom';
import './WhyItMatters.css';
import SEO from '../../components/SEO';
import whyFeatureImg from '../../assets/YOCILAB/IMG_3206 (1).jpg';
import communityImg from '../../assets/YOCILAB Webiste photos/Academy Home/ABETA YOCILAB Academy/REMS9766.jpg';

const pillars = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    title: "The Youth Dividend",
    highlight: "60%+ under age 25",
    desc: "Africa is the world's youngest continent. Over 60% of the population is under 25, representing the largest workforce and greatest creative powerhouse the world has ever seen."
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
      </svg>
    ),
    title: "The Opportunity Gap",
    highlight: "Skills vs Jobs mismatch",
    desc: "Millions graduate without practical, market-aligned digital and enterprise skills. Without access to mentorship, tools, and seed financing, bold ideas remain unrealized."
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    title: "The YOCILAB Solution",
    highlight: "Ecosystem approach",
    desc: "We provide hands-on Academy training, AI builder pathways, seed venture funding, and signature global summits to turn potential into tangible economic and community impact."
  }
];

const impacts = [
  { number: "3,500+", label: "Youth Upskilled", sub: "Across digital & entrepreneurial tracks" },
  { number: "80+", label: "Ventures Incubated", sub: "Creating local jobs & solutions" },
  { number: "$50,000+", label: "Seed Capital Unlocked", sub: "Directly deployed to youth innovators" },
  { number: "15+", label: "Institutional Partners", sub: "Universities, tech hubs & NGOs" }
];

const WhyItMatters = () => {
  return (
    <div className="why-it-matters-page">
      <SEO
        title="Why It Matters | YOCILAB"
        description="Discover why investing in Africa's youth innovators matters. We bridge the opportunity gap with skills, community, and capital."
      />

      {/* Hero Section */}
      <section className="why-hero">
        <div className="why-hero__bg">
          <img src={whyFeatureImg} alt="" className="why-hero__bg-img" />
          <div className="why-hero__overlay" />
        </div>
        <div className="container">
          <div className="why-hero__badge">
            <span className="why-hero__badge-dot" />
            OUR PURPOSE & CONVICTION
          </div>
          <h1 className="why-hero__title">
            Africa's Future is Built by<br />
            <em>Empowered Youth</em>
          </h1>
          <p className="why-hero__subtitle">
            When young people gain the skills, tools, and capital to solve problems in their own communities, they don't just change their own lives — they transform the trajectory of entire nations.
          </p>
          <div className="why-hero__actions">
            <Link to="/donate" className="btn btn-accent">
              Support Our Mission
            </Link>
            <Link to="/programs/yocilab-academy" className="btn btn-outline-white">
              Explore Our Programs
            </Link>
          </div>
        </div>
      </section>

      {/* Three Pillars Grid */}
      <section className="why-pillars-section">
        <div className="container">
          <div className="why-pillars-grid">
            {pillars.map((pillar, idx) => (
              <div key={idx} className="why-pillar-card">
                <div className="why-pillar-card__icon-wrap">
                  {pillar.icon}
                </div>
                <span className="why-pillar-card__highlight">{pillar.highlight}</span>
                <h3 className="why-pillar-card__title">{pillar.title}</h3>
                <p className="why-pillar-card__desc">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Story / Context */}
      <section className="why-story-section">
        <div className="container">
          <div className="why-story-grid">
            <div className="why-story-content">
              <span className="section-pill">THE MULTIPLIER EFFECT</span>
              <h2 className="why-story-title">
                Every $1 invested in youth skills yields up to $4 in economic impact.
              </h2>
              <p className="why-story-text">
                At YOCILAB, we see youth not as passive beneficiaries, but as primary architects of Africa's digital transformation, green industrialization, and civic resilience.
              </p>
              <p className="why-story-text">
                Without deliberate intervention, millions of talented young minds are left stranded by outdated education and systemic barriers to entry. By providing a launchpad of rigorous training, co-working space, AI tools, and early-stage capital, we unlock an unstoppable flywheel of local innovation.
              </p>
              
              <div className="why-quote-box">
                <p className="why-quote-text">
                  "Talent is evenly distributed across Africa, but opportunity is not. When we democratize access to builder tools and capital, young Africans solve the continent's toughest challenges."
                </p>
                <span className="why-quote-author">— YOCILAB Leadership Conviction</span>
              </div>
            </div>

            <div className="why-story-visual">
              <div className="why-visual-frame">
                <img 
                  src={whyFeatureImg} 
                  alt="Young African innovators collaborating" 
                  className="why-visual-img" 
                  loading="lazy"
                />
              </div>
              <div className="why-stat-badge">
                <span className="why-stat-num">1 in 3</span>
                <span className="why-stat-label">Global youth will be African by 2050</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Metrics Strip */}
      <section className="why-impact-section">
        <div className="container">
          <div className="why-impact-header">
            <span className="section-pill">OUR TRACK RECORD</span>
            <h2>Measurable Impact on the Ground</h2>
            <p>Direct outcomes created across grassroots programs and pan-African cohorts.</p>
          </div>

          <div className="mosaic-grid">
            <div className="mosaic-tile mosaic-tile--photo">
              <img src={whyFeatureImg} alt="Young African innovators collaborating" loading="lazy" />
            </div>
            <div className="mosaic-tile mosaic-tile--stat-light">
              <span className="mosaic-tile__value">{impacts[0].number}</span>
              <span className="mosaic-tile__label">{impacts[0].label}</span>
            </div>
            <div className="mosaic-tile mosaic-tile--stat-dark" style={{ background: 'var(--color-primary)' }}>
              <span className="mosaic-tile__value">{impacts[1].number}</span>
              <span className="mosaic-tile__label">{impacts[1].label}</span>
            </div>
            <div className="mosaic-tile mosaic-tile--stat-dark" style={{ background: 'var(--color-accent)', color: 'var(--color-dark)' }}>
              <span className="mosaic-tile__value">{impacts[2].number}</span>
              <span className="mosaic-tile__label" style={{ color: 'var(--color-dark)' }}>{impacts[2].label}</span>
            </div>
            <div className="mosaic-tile mosaic-tile--photo">
              <img src={communityImg} alt="Youth cohort at work" loading="lazy" />
            </div>
            <div className="mosaic-tile mosaic-tile--stat-light">
              <span className="mosaic-tile__value">{impacts[3].number}</span>
              <span className="mosaic-tile__label">{impacts[3].label}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Grid Section */}
      <section className="why-community-section">
        <div className="container">
          <div className="why-community-banner">
            <div className="why-community-img-wrap">
              <img src={communityImg} alt="Youth cohort at work" className="why-community-img" loading="lazy" />
            </div>
            <div className="why-community-text">
              <span className="section-pill">JOIN THE COMMUNITY</span>
              <h3>Be part of the generation building Africa's future.</h3>
              <p>
                Whether you're a student eager to learn, an entrepreneur seeking seed funding, or a mentor looking to give back — there's a place for you at YOCILAB.
              </p>
              <div className="why-community-buttons">
                <Link to="/programs/abeta" className="btn btn-primary">
                  Start Learning with ABETA
                </Link>
                <Link to="/youth-summit" className="btn btn-secondary">
                  Youth Summit 2025
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyItMatters;